import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowLeft, MapPin, Building2, Calendar, Ruler, Layers, Maximize2, Eye, Users, MessageCircle, Phone, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";

const WHATSAPP_NUMBER = "918758115155";

export default function RetailLeasingDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get(`/retail-properties/${id}`).then((r) => setP(r.data)).catch(() => setP(false));
  }, [id]);

  if (p === false) return <div className="container-zelo py-20" data-testid="retail-not-found">Space not found.</div>;
  if (!p) return <div className="container-zelo py-20" data-testid="retail-loading">Loading…</div>;

  const idShort = p?.code || `ZRL${(p?.id || "").slice(0, 4).toUpperCase()}`;

  return (
    <div className="bg-white" data-testid="retail-detail-page">
      <div className="container-zelo py-8">
        <Link to="/retail-leasing" className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-brand transition" data-testid="retail-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to all retail spaces
        </Link>
      </div>

      <div className="container-zelo grid grid-cols-1 lg:grid-cols-12 gap-8 pb-16">
        <div className="lg:col-span-7">
          <div className="rounded-2xl overflow-hidden bg-neutral-100 aspect-[4/3] relative">
            <img src={p.facade_image} alt="" className="w-full h-full object-cover" />
            <div className="absolute bottom-4 left-4 inline-flex items-center gap-2 bg-white/95 backdrop-blur px-4 py-2 rounded-full shadow-soft">
              <span className="text-brand font-bold text-sm">{idShort}</span>
              <span className="text-neutral-300">·</span>
              <span className="text-sm text-neutral-700">{p.possession}</span>
            </div>
          </div>

          <div className="mt-6">
            <div className="label-tiny text-brand">{p.property_type}</div>
            <h1 className="font-heading text-3xl md:text-4xl font-medium tracking-tight mt-2">{p.area}, {p.city}</h1>
            <div className="flex items-center gap-2 text-neutral-500 mt-2 text-sm"><MapPin className="w-4 h-4" />{p.state}</div>
          </div>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="retail-specs">
            <Spec Icon={Layers} l="Floor" v={p.floor} accent />
            <Spec Icon={Maximize2} l="Total Carpet" v={`${p.carpet_total.toLocaleString("en-IN")} sqft`} />
            <Spec Icon={Ruler} l="Frontage" v={`${p.frontage_ft} ft`} />
            <Spec Icon={Eye} l="Floor-to-Floor Height" v={`${p.height_ft} ft`} />
            <Spec Icon={Calendar} l="Possession" v={p.possession} />
            <Spec Icon={Building2} l="Layout" v={p.layout} />
          </div>

          <div className="mt-6 rounded-2xl border border-neutral-200 bg-neutral-50 p-6">
            <div className="label-tiny text-brand">Carpet Area Breakup</div>
            <div className="font-heading text-xl font-medium mt-2">{p.carpet_breakup}</div>
          </div>

          {p.nearby_tenants?.length > 0 && (
            <div className="mt-8" data-testid="retail-nearby-tenants">
              <div className="label-tiny text-brand mb-3 flex items-center gap-2"><Users className="w-3.5 h-3.5" /> Nearby Tenants</div>
              <div className="flex flex-wrap gap-2">
                {p.nearby_tenants.map((t, i) => (
                  <span key={i} className="px-3 py-1.5 rounded-full bg-brand-50 text-brand text-sm font-medium">{t}</span>
                ))}
              </div>
            </div>
          )}

          {p.description && (
            <div className="mt-8">
              <div className="label-tiny text-brand">About this space</div>
              <p className="mt-3 text-neutral-700 leading-relaxed">{p.description}</p>
            </div>
          )}

          {p.gallery?.length > 0 && (
            <div className="mt-10">
              <div className="label-tiny text-brand mb-3">Facade & Interior Drawings</div>
              <div className="grid grid-cols-2 gap-4">
                {p.gallery.map((g, i) => (
                  <div key={i} className="rounded-2xl overflow-hidden bg-neutral-100 aspect-[4/3]">
                    <img src={g} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <aside className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <div className="rounded-2xl border border-neutral-200 bg-white shadow-soft overflow-hidden">
              <div className="bg-gradient-to-br from-brand to-brand-600 text-white p-6">
                <div className="text-xs uppercase tracking-[0.18em] opacity-80">Asking Rent</div>
                <div className="font-heading text-4xl font-medium mt-1">₹{p.rent_per_sqft}<span className="text-base opacity-80">/sqft</span></div>
                <div className="text-sm opacity-80 mt-1">≈ ₹{Math.round(p.asking_total_rent).toLocaleString("en-IN")}/month total</div>
                <div className="mt-5 grid grid-cols-2 gap-3">
                  <Mini l="Floor" v={p.floor} />
                  <Mini l="Carpet" v={`${p.carpet_total} sqft`} />
                  <Mini l="Frontage" v={`${p.frontage_ft} ft`} />
                  <Mini l="Possession" v={p.possession} />
                </div>
              </div>
              <div className="p-6 space-y-3">
                <button onClick={() => setOpen(true)} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white px-5 py-3 text-sm font-semibold hover:bg-brand-600 transition active:scale-95" data-testid="retail-inquire-btn">
                  Interested? Inquire Now <ArrowRight className="w-4 h-4" />
                </button>
                <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hi Zelo, I'm interested in ${idShort} (${p.area}, ${p.city})`)}`} target="_blank" rel="noopener noreferrer" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 text-white px-5 py-3 text-sm font-semibold hover:bg-emerald-600 transition" data-testid="retail-whatsapp-btn">
                  <MessageCircle className="w-4 h-4" /> WhatsApp the Team
                </a>
                <a href="tel:+918758115155" className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-neutral-200 px-5 py-3 text-sm font-semibold hover:border-brand hover:text-brand transition" data-testid="retail-call-btn">
                  <Phone className="w-4 h-4" /> Call Leasing Desk
                </a>
              </div>
              <div className="border-t border-neutral-100 p-6 space-y-3 text-sm text-neutral-600">
                <Trust t={`${p.area}, ${p.city}, ${p.state}`} />
                <Trust t={`Possession ${p.possession}`} />
                <Trust t={p.layout} />
              </div>
            </div>
          </div>
        </aside>
      </div>

      <InquireModal open={open} setOpen={setOpen} property={p} idShort={idShort} />
    </div>
  );
}

function Spec({ Icon, l, v, accent }) {
  return (
    <div className={`rounded-2xl border ${accent ? "border-brand/40 bg-brand-50" : "border-neutral-200 bg-white"} p-4`}>
      <div className="flex items-center gap-2 label-tiny"><Icon className="w-3.5 h-3.5" /> {l}</div>
      <div className={`font-heading text-base md:text-lg font-medium tracking-tight mt-1.5 ${accent ? "text-brand-700" : "text-neutral-900"}`}>{v}</div>
    </div>
  );
}
function Mini({ l, v }) { return (<div><div className="text-[10px] uppercase tracking-[0.2em] opacity-70">{l}</div><div className="font-medium text-sm mt-0.5">{v}</div></div>); }
function Trust({ t }) { return (<div className="flex items-center gap-3"><span className="w-8 h-8 rounded-lg bg-brand-50 text-brand flex items-center justify-center"><CheckCircle2 className="w-4 h-4" /></span><span>{t}</span></div>); }

function InquireModal({ open, setOpen, property, idShort }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", company: "", brand: "", requirement: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const close = () => { setOpen(false); setTimeout(() => { setDone(false); setForm({ name: "", phone: "", email: "", company: "", brand: "", requirement: "" }); }, 300); };
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { toast.error("Name and phone are required"); return; }
    setLoading(true);
    try {
      await api.post("/retail-properties/inquire", { ...form, retail_property_id: property.id });
      setDone(true);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to submit"); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
      <DialogContent className="max-w-lg p-0 overflow-hidden border-0 rounded-2xl" data-testid="retail-inquire-modal">
        <DialogTitle className="sr-only">Inquire — {idShort}</DialogTitle>
        <DialogDescription className="sr-only">Leave your details for a callback from the Zelo retail leasing team.</DialogDescription>
        {done ? (
          <div className="p-10 text-center" data-testid="retail-inquire-success">
            <div className="w-16 h-16 rounded-full bg-brand-50 text-brand mx-auto flex items-center justify-center mb-5"><CheckCircle2 className="w-8 h-8" /></div>
            <div className="font-heading text-2xl font-medium">We're on it.</div>
            <div className="text-neutral-500 mt-2">Our retail leasing advisor will call you within 24 hours with the floor plan and lease terms.</div>
            <button onClick={close} className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand text-white px-6 py-2.5 text-sm font-semibold hover:bg-brand-600 transition">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-8" data-testid="retail-inquire-form">
            <div className="font-heading text-2xl font-medium tracking-tight">Inquire — {idShort}</div>
            <div className="text-sm text-neutral-500 mt-1">{property.area}, {property.city}</div>
            <div className="mt-5 space-y-3">
              <input className={inp} placeholder="Your Name *" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="inq-name" />
              <div className="grid grid-cols-2 gap-3">
                <input className={inp} placeholder="Phone *" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="inq-phone" />
                <input className={inp} placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="inq-email" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input className={inp} placeholder="Company" value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="inq-company" />
                <input className={inp} placeholder="Brand" value={form.brand} onChange={(e) => set("brand", e.target.value)} data-testid="inq-brand" />
              </div>
              <textarea rows={3} className={inp} placeholder="Requirement (optional)" value={form.requirement} onChange={(e) => set("requirement", e.target.value)} data-testid="inq-requirement" />
            </div>
            <button disabled={loading} className="mt-6 w-full rounded-full bg-brand text-white font-semibold py-3 hover:bg-brand-600 transition disabled:opacity-60 flex items-center justify-center gap-2" data-testid="inq-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send Inquiry <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
const inp = "w-full px-4 py-3 rounded-xl border border-neutral-200 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 text-sm bg-white";
